<template>
    <SectionContentLayout
      :state="contacts_controller.state.events['fetch_foreground']"
      content_class="contacts-person-form"
      invalid_text='contacts.person.invalid'
      invalid_icon='fas fa-address-card'
      error_text="contacts.list.error"
    >
      <template #close>
        <ActionButton
            @click="onClose()"
            class="back"
            icon="fas fa-chevron-circle-left"
            v-tooltip='"global.actions.back"'
        />
      </template>

      <template #header>
          <ContentTitle
            :title="route.name === 'contacts-person-add' ? 'contacts.form.person_add' : 'contacts.actions.edit_person'"
          />
      </template>

      <template #actions_success>
          <ActionButton
            @click="onContactsSave()"
            v-tooltip='"global.actions.save"'
            icon='fas fa-save'
            :disabled="contacts_controller.state.events['save_person'] === 'loading'"
          />
      </template>

      <template #content_success>
        <form>
          <fieldset @keyup.enter="onContactsSave()">
              <InputText
                input_id="cp_name"
                input_name="name"
                :input_required="true"
                :input_focus="true"
                v-model:input_value="contact.name"
                :input_value="contact.name"
                label_text="contacts.form.name"
                label_tooltip="contacts.form.required_field"
                error_text="contacts.form.error_required"
                :input_invalids="properties_validation.invalid"
                @change="validateAfterSubmit()">
              </InputText>

              <InputText
                input_id="cp_email"
                input_name="email"
                :input_value="contact.email"
                v-model:input_value="contact.email"
                label_text="contacts.form.email"
                error_text="contacts.form.error_email"
                :input_invalids="properties_validation.invalid"
                @change="validateAfterSubmit()">
              </InputText>

              <InputText
                input_id="cp_mobile_phone"
                input_name="mobile_phone"
                :input_value="contact.mobile_phone"
                v-model:input_value="contact.mobile_phone"
                label_text="contacts.form.mobile_phone"
                error_text="contacts.form.error_mobile"
                :input_invalids="properties_validation.invalid"
                @change="validateAfterSubmit()">
              </InputText>

              <InputTextArea
                input_id="cp_home_address"
                input_name="home_address"
                label_text="contacts.form.home_address"
                :input_value="contact.home_address"
                v-model:input_value="contact.home_address">
              </InputTextArea>

              <InputText
                input_id="cp_company"
                input_name="company"
                v-model:input_value="contact.company"
                :input_value="contact.company"
                label_text="contacts.form.company">
              </InputText>

              <InputDate
                input_id="cp_birth_date"
                input_name="birth_date"
                label_text="contacts.form.birth_date"
                :input_invalids="properties_validation.invalid"
                :input_value="contact.birth_date"
                @update:input_value="value => {contact.birth_date = value; validateAfterSubmit();}">
              </InputDate>

              <InputTextArea
                input_id="cp_notes"
                input_name="notes"
                label_text="contacts.form.notes"
                v-model:input_value="contact.notes">
              </InputTextArea>

          </fieldset>
          <div class="submit-desktop">
            <ActionButton
              @click='onContactsSave()'
              class="main-action"
              icon="fas fa-save"
              text="global.actions.save"
              :loading="contacts_controller.state.events['save_person'] === 'loading'"
            />
          </div>
        </form>
      </template>
    </SectionContentLayout>
</template>

<script setup>
  import {ref,reactive}               from 'vue';
  import {onBeforeMount, onUnmounted} from 'vue';
  import {defineProps}                from 'vue';
  import {useRoute,useRouter}         from 'vue-router';
  import SectionContentLayout         from '@/vue/Layouts/Sections/SectionContentLayout.vue';
  import ContentTitle                 from '@/vue/Elements/Content/ContentTitle.vue';
  import ActionButton                 from '@/vue/Elements/Buttons/ActionButton.vue';
  import ContactsController           from '@/js/Contacts/ContactsController';
  import PropertiesValidation         from '@/js/Shared/Validation/PropertiesValidation';
  import InputText                    from '@/vue/Elements/Inputs/InputText.vue';
  import InputTextArea from '@/vue/Elements/Inputs/InputTextArea.vue';
  import InputDate from '@/vue/Elements/Inputs/InputDate.vue';

  const props = defineProps({
    contacts_view : {}
  })

  const route                 = useRoute();
  const router                = useRouter();
  const contacts_controller   = ContactsController
  const contact               = ref();
  const first_submit          = ref(true);
  const properties_validation = reactive(new PropertiesValidation({
    name:  {
      required: true,
    },
    email:  {
      regex: 'email',
    },
    mobile_phone: {
      regex: 'phone',
    },
    birth_date:  {
      regex: 'birth_date',
    },
  }));

  const refs = {
   'name'       : ref(),
   'email'      : ref(),
   'phone'      : ref(),
   'birth_date' : ref(),
  };

  onBeforeMount(async () => {
    contacts_controller.state.on('fetch_foreground', 'success', onLoad);
    contacts_controller.broadcaster.on('contacts-deleted', onContactsDeleted);
  });

  onUnmounted(async () => {
    contacts_controller.state.destroy('fetch_foreground', 'success', onLoad);
    contacts_controller.broadcaster.destroy('contacts-deleted', onContactsDeleted);
  });

  const onLoad = () => {
    setContact();
  }

  const onClose = () => {
    close();
  }

  const onContactsDeleted = (contact_ids) => {
    if(contact_ids.includes(route.params.contact_id)){
      close();
    }
  }

  const onContactsSave = async () => {
    setFirstSubmit(false);

    if (validateFields() === false) {
      scrollToRef(properties_validation.getFirstInvalid());
      return;
    }

    if (await contacts_controller.savePerson(contact.value) === false) {
      return;
    }

    let id = route.params.contact_id || contacts_controller.contacts.all.getLastAdded().id;
    router.push('/contacts/person/info/' + id);
  }

  const onContactsErrorRetry = async () => {
    await props.contacts_view.fetchContacts();
  }

  const setContact = () => {
    contact.value = contacts_controller.contacts.getPersonClone(route.params.contact_id || null);
  }

  const scrollToRef = (id) => {
    document.getElementById(`cp_${id}`).scrollIntoView({block: "end", behavior: "smooth"});
  }

  const validateFields = () => {
    return properties_validation.validateObject(contact.value);
  }

  const validateAfterSubmit = () => {
    if (first_submit.value === true) {
      return;
    }

    return validateFields();
  }

  const setFirstSubmit = (value) => {
    first_submit.value = value;
  }

  const close = () => {
    let path = '/contacts';

    if (route.params.contact_id) {
      path += '/person/info/' + route.params.contact_id;
    }

    router.push(path);
  }
</script>